import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="ui--schema-form"
//
// Behaviour for Ui::SchemaFormComponent:
//   - error focus by JSON Pointer: on connect focuses the `focus` pointer, or
//     the first invalid field; `ui--schema-form:focus` ({ detail: { pointer } })
//     and the error-summary buttons refocus later. A pointer with no focusable
//     field falls back to its nearest ancestor pointer.
//   - oneOf switching: the discriminator select shows its branch's fieldset and
//     disables the others, so only the active branch is submitted.
//   - array of scalars: add / remove rows from the server-rendered <template>,
//     keeping each row's pointer and aria-label in step with its position.
//     Dispatches `ui--schema-form:items-changed` ({ detail: { pointer } }).
export default class extends Controller {
  static values = { focus: String }

  connect() {
    // Sync add/remove disabled state with the server-rendered row counts.
    this.element.querySelectorAll('[data-ui--schema-form-target="array"]').forEach((array) => this.reindex(array))

    if (this.focusValue) {
      this.focusPointer(this.focusValue)
    } else {
      const invalid = this.fieldElements().find((el) => el.getAttribute("aria-invalid") === "true")
      if (invalid) this.focusElement(invalid)
    }
  }

  focusEvent(event) {
    const pointer = event.detail && event.detail.pointer
    if (typeof pointer === "string") this.focusPointer(pointer)
  }

  focusError(event) {
    this.focusPointer(event.currentTarget.dataset.focusPointer || "")
  }

  // Focus the field at `pointer`, else the closest ancestor that can take
  // focus. Returns true when something was focused.
  focusPointer(pointer) {
    let current = pointer
    for (;;) {
      const el = this.fieldElements().find((candidate) => candidate.dataset.pointer === current)
      if (el) {
        this.focusElement(el)
        return true
      }
      const cut = current.lastIndexOf("/")
      if (cut < 0) return false
      current = current.slice(0, cut)
    }
  }

  switchVariant(event) {
    const select = event.target
    const group = select && select.dataset && select.dataset.oneOf
    if (!group) return

    this.element.querySelectorAll("[data-one-of-panel]").forEach((panel) => {
      if (panel.dataset.oneOfPanel !== group) return
      const active = panel.dataset.variant === select.value
      panel.hidden = !active
      panel.disabled = !active
    })
  }

  addItem(event) {
    const array = this.arrayFor(event.currentTarget)
    if (!array) return
    const max = this.limit(array, "maxItems")
    if (max !== null && this.rows(array).length >= max) return

    const source = array.querySelector("template[data-array-template]")
    const rows = array.querySelector("[data-array-rows]")
    if (!source || !rows) return

    const uid = Number(array.dataset.nextUid || this.rows(array).length)
    array.dataset.nextUid = String(uid + 1)
    const holder = document.createElement("template")
    holder.innerHTML = source.innerHTML.split("__UID__").join(String(uid))
    const added = holder.content.firstElementChild
    rows.appendChild(holder.content)

    this.reindex(array)
    const field = added && added.querySelector("[data-pointer]")
    if (field) this.focusElement(field)
    this.announce(array)
  }

  removeItem(event) {
    const array = this.arrayFor(event.currentTarget)
    const row = event.currentTarget.closest("[data-array-row]")
    if (!array || !row) return
    const min = this.limit(array, "minItems") || 0
    if (this.rows(array).length <= min) return

    row.remove()
    this.reindex(array)
    this.announce(array)
  }

  // --- helpers ---

  fieldElements() {
    return Array.from(this.element.querySelectorAll("[data-pointer]")).filter(
      (el) => !el.closest("fieldset:disabled") && !el.closest("[hidden]")
    )
  }

  focusElement(el) {
    if (el.tomselect && typeof el.tomselect.focus === "function") {
      el.tomselect.focus()
    } else {
      el.focus()
    }
  }

  arrayFor(el) {
    return el.closest('[data-ui--schema-form-target="array"]')
  }

  rows(array) {
    return Array.from(array.querySelectorAll("[data-array-rows] > [data-array-row]"))
  }

  limit(array, key) {
    const raw = array.dataset[key]
    if (raw === undefined || raw === "") return null
    const n = Number(raw)
    return Number.isFinite(n) ? n : null
  }

  reindex(array) {
    const rows = this.rows(array)
    rows.forEach((row, index) => {
      row.querySelectorAll("[data-pointer-template]").forEach((el) => {
        el.dataset.pointer = el.dataset.pointerTemplate.split("__INDEX__").join(String(index))
        if (el.dataset.labelTemplate) {
          el.setAttribute("aria-label", el.dataset.labelTemplate.split("__N__").join(String(index + 1)))
        }
      })
    })

    const max = this.limit(array, "maxItems")
    const min = this.limit(array, "minItems") || 0
    const add = array.querySelector("[data-array-add]")
    if (add) add.disabled = max !== null && rows.length >= max
    rows.forEach((row) => {
      const remove = row.querySelector("[data-array-remove]")
      if (remove) remove.disabled = rows.length <= min
    })
  }

  announce(array) {
    this.dispatch("items-changed", { detail: { pointer: array.dataset.pointer } })
  }
}
